import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Texting (Twilio SMS) is temporarily disabled — flip to true to re-enable the SMS UI.
export const SMS_ENABLED = false;

const client = axios.create({ baseURL: API, withCredentials: true });

// --- Keep users signed in: auto-refresh the access token on 401, then retry once ---
let refreshPromise = null;
const AUTH_SKIP = ["/auth/login", "/auth/refresh", "/auth/logout"];

client.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    if (status === 401 && original && !original._retry && !AUTH_SKIP.some((p) => url.includes(p))) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = client.post("/auth/refresh").finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return client(original);
      } catch (e) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

export const fmtDuration = (mins) => {
  if (mins == null) return "—";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "—";

export const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export const api = {
  // auth
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),

  // clients
  listClients: (search) => client.get("/clients", { params: { search } }).then((r) => r.data),
  getClient: (id) => client.get(`/clients/${id}`).then((r) => r.data),
  clientActivity: (id) => client.get(`/clients/${id}/activity`).then((r) => r.data),
  clientPhotos: (id) => client.get(`/clients/${id}/photos`).then((r) => r.data),
  createClient: (data) => client.post("/clients", data).then((r) => r.data),
  updateClient: (id, data) => client.put(`/clients/${id}`, data).then((r) => r.data),
  deleteClient: (id) => client.delete(`/clients/${id}`).then((r) => r.data),
  patchClientNotes: (id, notes) => client.patch(`/clients/${id}/notes`, { notes }).then((r) => r.data),

  // services
  listServices: () => client.get("/services").then((r) => r.data),
  createService: (data) => client.post("/services", data).then((r) => r.data),
  updateService: (id, data) => client.put(`/services/${id}`, data).then((r) => r.data),
  deleteService: (id) => client.delete(`/services/${id}`).then((r) => r.data),

  // quotes
  listQuotes: (status) => client.get("/quotes", { params: { status } }).then((r) => r.data),
  deleteQuote: (id) => client.delete(`/quotes/${id}`).then((r) => r.data),
  getQuote: (id) => client.get(`/quotes/${id}`).then((r) => r.data),
  createQuote: (data) => client.post("/quotes", data).then((r) => r.data),
  updateQuote: (id, data) => client.put(`/quotes/${id}`, data).then((r) => r.data),
  patchQuoteNotes: (id, notes) => client.patch(`/quotes/${id}/notes`, { notes }).then((r) => r.data),
  setQuoteStatus: (id, status) => client.post(`/quotes/${id}/status`, null, { params: { status } }).then((r) => r.data),
  sendQuote: (id) => client.post(`/quotes/${id}/send`).then((r) => r.data),
  convertQuote: (id) => client.post(`/quotes/${id}/convert`).then((r) => r.data),
  publicQuote: (id) => client.get(`/public/quotes/${id}`).then((r) => r.data),
  approveQuote: (id, data) => client.post(`/public/quotes/${id}/approve`, data).then((r) => r.data),
  declineQuote: (id, reason) => client.post(`/public/quotes/${id}/decline`, { reason }).then((r) => r.data),
  signatureUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/public/quotes/${id}/signature`,
  satelliteUrl: (address, opts = {}) => {
    const p = new URLSearchParams({ address, zoom: opts.zoom || 19, width: opts.width || 600, height: opts.height || 360 });
    return `${process.env.REACT_APP_BACKEND_URL}/api/maps/satellite?${p.toString()}`;
  },

  // sms
  smsStatus: () => client.get("/sms/status").then((r) => r.data),
  sendSms: (data) => client.post("/sms/send", data).then((r) => r.data),
  smsHistory: (clientId) => client.get(`/sms/history/${clientId}`).then((r) => r.data),
  smsConversations: () => client.get("/sms/conversations").then((r) => r.data),
  smsThread: (phone) => client.get("/sms/thread", { params: { phone } }).then((r) => r.data),
  smsUnread: () => client.get("/sms/unread-count").then((r) => r.data),

  // jobs
  listJobs: (params) => client.get("/jobs", { params }).then((r) => r.data),
  getJob: (id) => client.get(`/jobs/${id}`).then((r) => r.data),
  createJob: (data) => client.post("/jobs", data).then((r) => r.data),
  updateJob: (id, data) => client.put(`/jobs/${id}`, data).then((r) => r.data),
  patchJobNotes: (id, notes) => client.patch(`/jobs/${id}/notes`, { notes }).then((r) => r.data),
  setJobStatus: (id, status) => client.post(`/jobs/${id}/status`, null, { params: { status } }).then((r) => r.data),
  setJobSchedule: (id, data) => client.patch(`/jobs/${id}/schedule`, data).then((r) => r.data),
  listEvents: () => client.get("/events").then((r) => r.data),
  createEvent: (data) => client.post("/events", data).then((r) => r.data),
  updateEvent: (id, data) => client.patch(`/events/${id}`, data).then((r) => r.data),
  deleteEvent: (id) => client.delete(`/events/${id}`).then((r) => r.data),
  deleteJob: (id) => client.delete(`/jobs/${id}`).then((r) => r.data),
  invoiceFromJob: (id) => client.post(`/jobs/${id}/invoice`).then((r) => r.data),
  bulkJobs: (data) => client.post("/jobs/bulk", data).then((r) => r.data),

  // templates
  listTemplates: () => client.get("/job-templates").then((r) => r.data),
  createTemplate: (data) => client.post("/job-templates", data).then((r) => r.data),
  deleteTemplate: (id) => client.delete(`/job-templates/${id}`).then((r) => r.data),

  // photos
  listJobPhotos: (jobId) => client.get(`/jobs/${jobId}/photos`).then((r) => r.data),
  uploadJobPhoto: (jobId, formData) => client.post(`/jobs/${jobId}/photos`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  updatePhoto: (id, data) => client.patch(`/photos/${id}`, data).then((r) => r.data),
  deletePhoto: (id) => client.delete(`/photos/${id}`).then((r) => r.data),
  photoUrl: (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`,

  // invoices
  listInvoices: (status) => client.get("/invoices", { params: { status } }).then((r) => r.data),
  getInvoice: (id) => client.get(`/invoices/${id}`).then((r) => r.data),
  createInvoice: (data) => client.post("/invoices", data).then((r) => r.data),
  updateInvoice: (id, data) => client.put(`/invoices/${id}`, data).then((r) => r.data),
  sendInvoice: (id) => client.post(`/invoices/${id}/send`).then((r) => r.data),
  markPaid: (id, data) => client.post(`/invoices/${id}/mark-paid`, data || {}).then((r) => r.data),
  deleteInvoice: (id) => client.delete(`/invoices/${id}`).then((r) => r.data),
  publicInvoice: (id) => client.get(`/public/invoices/${id}`).then((r) => r.data),

  // users
  listUsers: (role) => client.get("/users", { params: { role } }).then((r) => r.data),
  createUser: (data) => client.post("/users", data).then((r) => r.data),
  updateUser: (id, data) => client.put(`/users/${id}`, data).then((r) => r.data),
  deleteUser: (id) => client.delete(`/users/${id}`).then((r) => r.data),

  // dashboard
  dashboard: () => client.get("/dashboard/stats").then((r) => r.data),

  // pipeline
  pipelineBoard: (type) => client.get("/pipeline/board", { params: { type } }).then((r) => r.data),
  pipelineMove: (data) => client.post("/pipeline/move", data).then((r) => r.data),
  pipelineQuickEdit: (data) => client.post("/pipeline/quick-edit", data).then((r) => r.data),

  // customer portal
  getPortal: (token) => client.get(`/portal/${token}`).then((r) => r.data),
  portalLink: (clientId) => client.get(`/clients/${clientId}/portal-link`).then((r) => r.data),
  resetPortalLink: (clientId) => client.post(`/clients/${clientId}/portal-link/reset`).then((r) => r.data),
  invoicePdfUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/pdf`,
  quotePdfUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${id}/pdf`,

  // reports, tags, import
  runReport: (data) => client.post("/reports/run", data).then((r) => r.data),
  clientTags: () => client.get("/client-tags").then((r) => r.data),
  deleteAllClients: () => client.delete("/clients").then((r) => r.data),
  importCsv: (kind, formData) => client.post(`/import/${kind}`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  importTemplateUrl: (kind) => `${process.env.REACT_APP_BACKEND_URL}/api/import/${kind}/template.csv`,

  // csv export
  downloadCsv: async (type) => {
    const res = await client.get(`/export/${type}.csv`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${type}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // time tracking
  timeActive: () => client.get("/time/active").then((r) => r.data),
  clockIn: (data) => client.post("/time/clock-in", data).then((r) => r.data),
  clockOut: (data) => client.post("/time/clock-out", data || {}).then((r) => r.data),
  myTime: () => client.get("/time/my").then((r) => r.data),
  allTime: (params) => client.get("/time/all", { params }).then((r) => r.data),
  timeSummary: () => client.get("/time/summary").then((r) => r.data),
  editTimeEntry: (id, data) => client.put(`/time/${id}`, data).then((r) => r.data),
  deleteTimeEntry: (id) => client.delete(`/time/${id}`).then((r) => r.data),

  // notifications
  notifSettings: () => client.get("/notifications/settings").then((r) => r.data),
  saveNotifSettings: (data) => client.put("/notifications/settings", data).then((r) => r.data),
  notifPreview: (data) => client.post("/notifications/preview", data).then((r) => r.data),
  notifSend: (data) => client.post("/notifications/send", data).then((r) => r.data),

  // payments
  checkout: (invoice_id) =>
    client.post("/payments/checkout", { invoice_id, origin_url: window.location.origin }).then((r) => r.data),
  depositCheckout: (quote_id) =>
    client.post("/payments/deposit-checkout", { quote_id, origin_url: window.location.origin }).then((r) => r.data),
  paymentStatus: (session_id) => client.get(`/payments/status/${session_id}`).then((r) => r.data),
};
